import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';

const ReportGenerator = () => {
  const [selectedReport, setSelectedReport] = useState('');
  const [reportConfig, setReportConfig] = useState({
    dateRange: '30days',
    format: 'pdf',
    includeCharts: true,
    includeRawData: false,
    recipients: '',
    schedule: 'manual'
  });

  const reportTypes = [
    {
      id: 'performance-summary',
      name: 'Performance Summary',
      icon: 'TrendingUp',
      description: 'Comprehensive overview of platform performance metrics',
      estimatedTime: '2-3 minutes',
      sections: ['Key Metrics', 'Growth Trends', 'Regional Analysis', 'Top Performers']
    },
    {
      id: 'financial-analysis',
      name: 'Financial Analysis',
      icon: 'DollarSign',
      description: 'Revenue tracking, commission analysis, and financial projections',
      estimatedTime: '3-4 minutes',
      sections: ['Revenue Breakdown', 'Commission Tracking', 'ROI Analysis', 'Forecasting']
    },
    {
      id: 'startup-ecosystem',
      name: 'Startup Ecosystem Report',
      icon: 'Building',
      description: 'Detailed analysis of startup activity and sector trends',
      estimatedTime: '4-5 minutes',
      sections: ['Startup Distribution', 'Sector Analysis', 'Growth Patterns', 'Success Rates']
    },
    {
      id: 'referral-performance',
      name: 'Referral Performance',
      icon: 'Users',
      description: 'Intermediary performance metrics and referral effectiveness',
      estimatedTime: '2-3 minutes',
      sections: ['Referral Metrics', 'Conversion Rates', 'Top Referrers', 'Quality Scores']
    },
    {
      id: 'program-effectiveness',
      name: 'Program Effectiveness',
      icon: 'Target',
      description: 'Corporate program success rates and optimization insights',
      estimatedTime: '3-4 minutes',
      sections: ['Program Metrics', 'Success Rates', 'Optimization Tips', 'Benchmarking']
    },
    {
      id: 'custom-report',
      name: 'Custom Report',
      icon: 'Settings',
      description: 'Build a custom report with specific metrics and timeframes',
      estimatedTime: '5-10 minutes',
      sections: ['Custom Metrics', 'Flexible Timeframes', 'Advanced Filters', 'Custom Charts']
    }
  ];

  const dateRanges = [
    { value: '7days', label: 'Last 7 days' },
    { value: '30days', label: 'Last 30 days' },
    { value: '90days', label: 'Last 3 months' },
    { value: '6months', label: 'Last 6 months' },
    { value: '1year', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  const formats = [
    { value: 'pdf', label: 'PDF Document', icon: 'FileText' },
    { value: 'excel', label: 'Excel Spreadsheet', icon: 'FileSpreadsheet' },
    { value: 'powerpoint', label: 'PowerPoint Presentation', icon: 'Presentation' },
    { value: 'csv', label: 'CSV Data', icon: 'Database' }
  ];

  const scheduleOptions = [
    { value: 'manual', label: 'Generate manually' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const handleGenerateReport = () => {
    console.log('Generating report:', selectedReport, reportConfig);
    // Report generation logic would go here
  };

  const selectedReportData = reportTypes?.find(report => report?.id === selectedReport);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Report Generator
        </h3>
        <p className="text-sm text-muted-foreground">
          Create comprehensive analytics reports with customizable parameters
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Type Selection */}
        <div className="lg:col-span-2">
          <h4 className="font-semibold text-foreground mb-4">Select Report Type</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {reportTypes?.map((report) => (
              <div
                key={report?.id}
                onClick={() => setSelectedReport(report?.id)}
                className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                  selectedReport === report?.id
                    ? 'bg-primary/10 border-primary' :'bg-background border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedReport === report?.id ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}>
                    <Icon name={report?.icon} size={20} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-foreground mb-1">{report?.name}</h5>
                    <p className="text-xs text-muted-foreground mb-2">{report?.description}</p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <Icon name="Clock" size={12} />
                        <span>{report?.estimatedTime}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Layers" size={12} />
                        <span>{report?.sections?.length} sections</span>
                      </div>
                    </div>
                  </div>
                  
                  {selectedReport === report?.id && (
                    <Icon name="Check" size={16} className="text-primary" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Report Sections Preview */}
          {selectedReportData && (
            <div className="bg-muted/50 rounded-lg p-4">
              <h5 className="font-medium text-foreground mb-3">Report Sections</h5>
              <div className="grid grid-cols-2 gap-2">
                {selectedReportData?.sections?.map((section, index) => (
                  <div key={section} className="flex items-center space-x-2 text-sm">
                    <div className="w-4 h-4 bg-primary/20 rounded flex items-center justify-center">
                      <span className="text-xs font-medium text-primary">{index + 1}</span>
                    </div>
                    <span className="text-foreground">{section}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Report Configuration */}
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-foreground mb-4">Configuration</h4>
            
            <div className="space-y-4">
              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Date Range
                </label>
                <select
                  value={reportConfig?.dateRange}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, dateRange: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {dateRanges?.map((range) => (
                    <option key={range?.value} value={range?.value}>
                      {range?.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Export Format
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {formats?.map((format) => (
                    <button
                      key={format?.value}
                      onClick={() => setReportConfig(prev => ({ ...prev, format: format?.value }))}
                      className={`flex items-center space-x-2 p-2 rounded-md text-xs font-medium transition-all duration-200 ${
                        reportConfig?.format === format?.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={format?.icon} size={14} />
                      <span>{format?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={reportConfig?.includeCharts}
                    onChange={(e) => setReportConfig(prev => ({ ...prev, includeCharts: e?.target?.checked }))}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Include charts and graphs</span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={reportConfig?.includeRawData}
                    onChange={(e) => setReportConfig(prev => ({ ...prev, includeRawData: e?.target?.checked }))}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Include raw data tables</span>
                </label>
              </div>

              {/* Email Recipients */}
              <Input
                label="Email Recipients"
                type="email"
                placeholder="email@example.com, email2@example.com"
                value={reportConfig?.recipients}
                onChange={(e) => setReportConfig(prev => ({ ...prev, recipients: e?.target?.value }))}
                description="Comma-separated email addresses"
              />

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Schedule
                </label>
                <select
                  value={reportConfig?.schedule}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, schedule: e?.target?.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {scheduleOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="pt-4 border-t border-border">
            <Button
              variant="default"
              fullWidth
              disabled={!selectedReport}
              onClick={handleGenerateReport}
            >
              <Icon name="FileText" size={16} className="mr-2" />
              Generate Report
            </Button>
            
            {selectedReportData && (
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Estimated generation time: {selectedReportData?.estimatedTime}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportGenerator;